import { Audience, Partner, ViewCategory, VisibilityType } from '@ddt-web/config';
import { Country } from '@ddt-web/domain';
import * as get from 'lodash/get';

import { IdMapperConfig, TabVisibilityConfigOptions, TabVisibleConfigOptions, VisibleConfigOptions } from '../models/visibility-config';

/*******************************************
 * HERE BE DRAGONS
 * DO NOT change the code below if it looks like it doesn't do what you need it to do.
 * Consult the team if there's functionality missing !!!
 *******************************************/

export interface TabConfigVisibilityKeyArgs {
	view?: ViewCategory[];
	country?: Country[];
	partner?: Partner[];
	visibility?: VisibilityType[];
}

/**
 * Static functions that contain visibility config key resolution logic
 */
export class VisibilityConfigUtils {

	/**
	 * Generates a string to be used as tab config visibility key
	 */
	public static getTabConfigVisibilityKey(
		tabConfigVisibilityKeyArgs: TabConfigVisibilityKeyArgs
	): string {
		// Filtering out falsy properties
		const configKeyAsObject: TabConfigVisibilityKeyArgs = Object.keys(tabConfigVisibilityKeyArgs)
			.filter((k) => !!tabConfigVisibilityKeyArgs[k])
			.reduce((a, k) => ({ ...a, [k]: tabConfigVisibilityKeyArgs[k] }), {});

		return JSON.stringify(configKeyAsObject);
	}

	/**
	 * Parses a tab config visibility key back into an object
	 */
	public static getTabConfigVisibilityObj(key: string): TabConfigVisibilityKeyArgs {
		try {
			return JSON.parse(key);
		} catch (e) {
			if (key !== 'default') {
				console.warn(`failed to convert key "${key}" to object, error: ${e}`);
			}
			return {};
		}
	}

	/**
	 * Resolves which config should be used based on the view and audience being passed
	 */
	public static selectDisplayableTab(
		tab: TabVisibilityConfigOptions | VisibleConfigOptions | { [viewCategory: string]: IdMapperConfig },
		view: ViewCategory,
		audience: Audience,
		customFallback?: TabVisibleConfigOptions | Object,
		visibility?: VisibilityType
	): TabVisibleConfigOptions | Object {

		const configKey = VisibilityConfigUtils.selectDisplayableTabKey(tab, view, audience, visibility);

		const fallback = customFallback || tab['default'];

		return tab[configKey] || fallback || {};
	}

	public static selectDisplayableTabKey(
		tab: TabVisibilityConfigOptions | VisibleConfigOptions | { [viewCategory: string]: IdMapperConfig },
		view: ViewCategory,
		audience: Audience,
		visibility?: VisibilityType
	): string {
		const matcher = {
			view: view,
			country: get(audience, 'country', null),
			partner: get(audience, 'partner', null),
			visibility: visibility
		};
		// filtering falsy values
		const filteredMatcher = Object.keys(matcher)
			.filter((k) => !!matcher[k])
			.reduce((a, k) => ({ ...a, [k]: matcher[k] }), {});

		// for each array of configs per tab, get the keys in object format
		const configKeysAsObj: TabConfigVisibilityKeyArgs[] = Object.keys(tab).map(key => VisibilityConfigUtils.getTabConfigVisibilityObj(key));

		// filter out configs that are not a match
		const filteredConfigs = configKeysAsObj.filter(configKeyObj => {
			let isMatch = true;
			Object.keys(configKeyObj)
				.forEach(attrKey => {
					if (Array.isArray(configKeyObj[attrKey])) {
						if (!configKeyObj[attrKey].includes(filteredMatcher[attrKey])) {
							isMatch = false;
						}
					} else {
						if (configKeyObj[attrKey] !== filteredMatcher[attrKey]) {
							isMatch = false;
						}
					}
				});
			return isMatch;
		});

		const resolvedConfig = filteredConfigs.reduce((previousMatch, configKeyObj) => {
			// for each config key obj, calculate number of matches
			const score = Object.keys(configKeyObj).reduce((acc, attrKey) => {
				if (Array.isArray(configKeyObj[attrKey])) {
					if (configKeyObj[attrKey].includes(filteredMatcher[attrKey])) {
						acc = acc + 1;
					}
				} else {
					if (configKeyObj[attrKey] === filteredMatcher[attrKey]) {
						acc = acc + 1;
					}
				}
				return acc;
			}, 0);
			// if the current config has a higher score, keep this one
			return score > previousMatch.score ? { configKey: JSON.stringify(configKeyObj), score: score } : previousMatch;

		}, { configKey: '', score: 0 });

		return resolvedConfig.configKey;
	}
}
